import { motion } from "framer-motion";
import { ExternalLink, Container, Ship, Search } from "lucide-react";
import { useState } from "react";
import { Input } from "@/components/ui/input";

const SHIPPING_LINES = [
  {
    name: "CMA CGM",
    code: "CMAU",
    url: "https://www.cma-cgm.com/ebusiness/tracking/search",
    country: "🇫🇷",
    note: "Major carrier on China-Djibouti route",
  },
  {
    name: "CU Lines",
    code: "CULU",
    url: "https://www.culines.com/en/site/bill",
    country: "🇨🇳",
    note: "China United Lines",
  },
  {
    name: "Maersk",
    code: "MAEU",
    url: "https://www.maersk.com/tracking/",
    country: "🇩🇰",
    note: "World's largest container shipping company",
  },
  {
    name: "MSC",
    code: "MSCU",
    url: "https://www.msc.com/en/track-a-shipment",
    country: "🇨🇭",
    note: "Mediterranean Shipping Company",
  },
  {
    name: "COSCO Shipping",
    code: "COSU",
    url: "https://elines.coscoshipping.com/ebusiness/cargoTracking",
    country: "🇨🇳",
    note: "China's largest shipping line",
  },
  {
    name: "Evergreen",
    code: "EGLV",
    url: "https://www.evergreen-line.com/tuf1/jsp/TUF1_CargoTracking.jsp",
    country: "🇹🇼",
    note: "Taiwanese carrier",
  },
  {
    name: "Hapag-Lloyd",
    code: "HLCU",
    url: "https://www.hapag-lloyd.com/en/online-business/track/track-by-container-solution.html",
    country: "🇩🇪",
    note: "German carrier",
  },
  {
    name: "ONE Line",
    code: "ONEY",
    url: "https://ecomm.one-line.com/one-ecom/manage-shipment/cargo-tracking",
    country: "🇯🇵",
    note: "Ocean Network Express",
  },
  {
    name: "Yang Ming",
    code: "YMLU",
    url: "https://www.yangming.com/e-service/Track_Trace/track_trace_cargo_tracking.aspx",
    country: "🇹🇼",
    note: "Taiwanese carrier",
  },
  {
    name: "ZIM",
    code: "ZIMU",
    url: "https://www.zim.com/tools/track-a-shipment",
    country: "🇮🇱",
    note: "Israeli carrier",
  },
  {
    name: "PIL",
    code: "PILU",
    url: "https://www.pilship.com/shared/ajax/?fn=containerFlashTracking",
    country: "🇸🇬",
    note: "Pacific International Lines",
  },
  {
    name: "ANL",
    code: "ANNU",
    url: "https://www.anl.com.au/ebusiness/tracking/search",
    country: "🇦🇺",
    note: "Part of CMA CGM Group",
  },
];

const UNIVERSAL_TRACKERS = [
  {
    name: "Track-Trace",
    desc: "Multi-carrier container tracking",
    url: "https://www.track-trace.com/container",
    icon: "🌐",
  },
  {
    name: "Searates",
    desc: "Universal container tracking + freight rates",
    url: "https://www.searates.com/container/tracking/",
    icon: "🚢",
  },
  {
    name: "VesselFinder",
    desc: "Live vessel tracking by ship name/IMO",
    url: "https://www.vesselfinder.com/",
    icon: "📍",
  },
  {
    name: "MarineTraffic",
    desc: "Real-time ship positions worldwide",
    url: "https://www.marinetraffic.com/en/ais/home/centerx:43.1/centery:12.5/zoom:5",
    icon: "🗺️",
  },
];

const TrackingPage = () => {
  const [search, setSearch] = useState("");
  
  const filtered = SHIPPING_LINES.filter(line =>
    line.name.toLowerCase().includes(search.toLowerCase()) ||
    line.code.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="trade-section pt-32 lg:pt-20 pb-20 min-h-screen">
      <div className="lg:ml-64 px-6 md:px-12 py-10">
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <p className="text-xs uppercase tracking-[0.3em] trade-gold mb-3">Tracking</p>
          <h1 className="text-4xl md:text-5xl font-serif font-bold">Container & B/L Tracking</h1>
          <p className="text-white/50 mt-2">Track containers across all major shipping lines</p>
        </motion.div>

        {/* Universal Trackers */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-10"
        >
          <h2 className="text-sm uppercase tracking-widest trade-gold mb-4">Universal Trackers</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {UNIVERSAL_TRACKERS.map((t, i) => (
              <motion.a
                key={t.name}
                href={t.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + i * 0.05 }}
                className="group trade-card rounded-sm p-5 relative overflow-hidden"
                data-testid={`universal-${t.name.toLowerCase()}`}
              >
                <div className="absolute inset-x-0 top-0 h-1 bg-[#10b981] opacity-50 group-hover:opacity-100 transition-opacity" />
                <div className="flex items-start justify-between mb-3">
                  <span className="text-3xl">{t.icon}</span>
                  <ExternalLink className="w-4 h-4 text-white/30 group-hover:trade-gold transition-colors" />
                </div>
                <h3 className="font-semibold mb-1">{t.name}</h3>
                <p className="text-xs text-white/50">{t.desc}</p>
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="trade-card rounded-sm p-4 mb-6"
        >
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search shipping line (e.g. CMA CGM, Maersk, COSCO)..."
              className="trade-input h-12 pl-11 rounded-sm"
              data-testid="search-carrier-input"
            />
          </div>
        </motion.div>

        {/* Shipping Lines */}
        <h2 className="text-sm uppercase tracking-widest trade-gold mb-4">Shipping Lines</h2>
        
        {filtered.length === 0 ? (
          <div className="trade-card rounded-sm p-16 text-center">
            <Ship className="w-12 h-12 trade-gold mx-auto mb-4 opacity-40" strokeWidth={1} />
            <p className="text-white/50">No carriers match "{search}"</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((line, i) => (
              <motion.a
                key={line.code}
                href={line.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.04, 0.6) }}
                className="group trade-card rounded-sm p-5"
                data-testid={`carrier-${line.code.toLowerCase()}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{line.country}</span>
                    <div>
                      <h3 className="font-serif font-semibold">{line.name}</h3>
                      <p className="text-[10px] uppercase tracking-widest trade-gold mt-0.5">{line.code}</p>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-white/30 group-hover:trade-gold transition-colors flex-shrink-0" />
                </div>
                <p className="text-xs text-white/50 mb-3">{line.note}</p>
                <div className="flex items-center gap-2 text-xs trade-gold group-hover:gap-3 transition-all pt-3 border-t border-white/5">
                  <Container size={14} />
                  <span>Track Container / B/L</span>
                </div>
              </motion.a>
            ))}
          </div>
        )}

        {/* Tip card */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="trade-card rounded-sm p-6 mt-8 bg-gradient-to-br from-[#D4AF37]/10 to-transparent"
        >
          <h3 className="text-sm uppercase tracking-widest trade-gold mb-3">💡 How to Use</h3>
          <ul className="text-sm text-white/70 space-y-2">
            <li>• Container numbers follow format: <span className="trade-gold font-mono">XXXU 1234567</span> (4 letters + 7 digits)</li>
            <li>• The first 4 letters identify the carrier (e.g. <span className="trade-gold">MAEU</span> = Maersk, <span className="trade-gold">CMAU</span> = CMA CGM)</li>
            <li>• B/L (Bill of Lading) numbers are provided by your shipper/freight forwarder</li>
            <li>• Use Universal Trackers if you don't know the carrier</li>
          </ul>
        </motion.div>

      </div>
    </div>
  );
};

export default TrackingPage;
